import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, MessageCircle, Video, Image, Clock, Bookmark, ChevronRight, Users, Cake } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import ApiService from '../services/ApiService';

const BATCH_SIZE = 20;

function HomeFeedView({
  relatives,
  families,
  selectedFamily,
  currentUserUuid,
  onShowToast,
  cachedContent,
  onContentLoaded,
  contentManifest,
  onManifestLoaded,
  onOpenCalloutResponses,
  onOpenCalloutsHistory,
}) {
  // manifest = lightweight UUID list from /checkForContentList (sorted newest first)
  const [manifest, setManifest] = useState(contentManifest || null);
  // resolvedContent = full content previews from /checkForContentBatch
  const [resolvedContent, setResolvedContent] = useState(cachedContent || []);
  const [loading, setLoading] = useState(!cachedContent && !contentManifest);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Track how many manifest items have been resolved
  const resolvedCountRef = useRef(cachedContent ? cachedContent.length : 0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  // Reset local state when parent clears cache (e.g. family switch)
  useEffect(() => {
    if (!cachedContent && !contentManifest) {
      setManifest(null);
      setResolvedContent([]);
      resolvedCountRef.current = 0;
      loadingRef.current = false;
      loadManifest();
    } else if (contentManifest && !cachedContent) {
      // Manifest exists but no resolved content — resolve first batch
      setManifest(contentManifest);
      resolveNextBatch(contentManifest, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatives, cachedContent, contentManifest]);

  const loadManifest = async () => {
    if (!relatives || relatives.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const relativeUuids = relatives.map(r => r.uuid);
      const data = await ApiService.getContentList(relativeUuids);
      const uuidList = data.contentUuids || [];

      // Deduplicate by uuid
      const seen = new Set();
      const deduped = uuidList.filter(entry => {
        if (seen.has(entry.uuid)) return false;
        seen.add(entry.uuid);
        return true;
      });

      setManifest(deduped);
      onManifestLoaded?.(deduped);

      // Resolve first batch
      await resolveNextBatch(deduped, 0);
    } catch (error) {
      onShowToast?.('Failed to load home feed', 'error');
    }
    setLoading(false);
  };

  const resolveNextBatch = async (manifestList, startOffset) => {
    if (loadingRef.current) return;
    if (startOffset >= manifestList.length) return;

    loadingRef.current = true;
    setLoadingMore(startOffset > 0);

    try {
      const batch = manifestList.slice(startOffset, startOffset + BATCH_SIZE);
      const uuids = batch.map(entry => entry.uuid);
      const data = await ApiService.getContentBatch(uuids);
      const previews = data.contentPreviewArray || [];

      resolvedCountRef.current = startOffset + batch.length;

      setResolvedContent(prev => {
        const existing = new Set(prev.map(c => c.uuid));
        const newItems = previews.filter(p => !existing.has(p.uuid));
        const updated = [...prev, ...newItems];
        // Update parent cache
        onContentLoaded?.(updated);
        return updated;
      });
    } catch (error) {
      onShowToast?.('Failed to load content', 'error');
    }

    loadingRef.current = false;
    setLoadingMore(false);
  };

  // IntersectionObserver for infinite scroll
  const handleSentinel = useCallback((node) => {
    if (sentinelRef.current) sentinelRef.current.disconnect();
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && manifest && resolvedCountRef.current < manifest.length) {
          resolveNextBatch(manifest, resolvedCountRef.current);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    sentinelRef.current = observer;
  }, [manifest]);

  const getRelativeName = (content) => {
    const rel = relatives.find(r => r.uuid === content.userUuid);
    return rel?.name || content.userName || 'Unknown';
  };

  const getRelativeImage = (content) => {
    const rel = relatives.find(r => r.uuid === content.userUuid);
    return rel?.profile?.profilePictureUrl || rel?.profilePictureUrl || null;
  };

  const isUrlExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    return Date.now() > expiry.getTime() - 10 * 60 * 1000;
  };

  const refreshUrlsIfNeeded = async (content) => {
    if (isUrlExpired(content.urlsExpiryDate)) {
      try {
        const refreshed = await ApiService.refreshContentUrls(content.uuid);
        setResolvedContent(prev => prev.map(c =>
          c.uuid === content.uuid ? { ...c, ...refreshed } : c
        ));
        return { ...content, ...refreshed };
      } catch (error) {
        onShowToast?.('Failed to refresh content', 'error');
      }
    }
    return content;
  };

  const openContent = async (content) => {
    const idx = displayContent.findIndex(c => c.uuid === content.uuid);
    const refreshed = await refreshUrlsIfNeeded(content);
    setCurrentVideoIndex(idx);
    setCurrentVideo(refreshed);
  };

  const playNext = () => {
    const nextIdx = (currentVideoIndex + 1) % displayContent.length;
    setCurrentVideoIndex(nextIdx);
    setCurrentVideo(displayContent[nextIdx]);
  };

  const playPrevious = () => {
    const prevIdx = currentVideoIndex === 0 ? displayContent.length - 1 : currentVideoIndex - 1;
    setCurrentVideoIndex(prevIdx);
    setCurrentVideo(displayContent[prevIdx]);
  };

  const handleToggleLike = async (contentUuid) => {
    const content = resolvedContent.find(c => c.uuid === contentUuid);
    if (!content) return;

    const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);
    try {
      const result = isLiked
        ? await ApiService.removeLike(contentUuid)
        : await ApiService.toggleLike(contentUuid);

      const update = (c) => c.uuid === contentUuid
        ? { ...c, likeCount: result.likeCount, likes: result.likes }
        : c;
      setResolvedContent(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({ ...prev, likeCount: result.likeCount, likes: result.likes }));
      }
    } catch (error) {
      onShowToast?.('Failed to update like', 'error');
    }
  };

  const handleToggleFavorite = async (contentUuid) => {
    const content = resolvedContent.find(c => c.uuid === contentUuid);
    if (!content) return;

    const isFav = !!content.favorite;
    try {
      await ApiService.toggleFavorite(contentUuid, isFav ? 'remove' : 'add');
      const update = (c) => c.uuid === contentUuid ? { ...c, favorite: !isFav } : c;
      setResolvedContent(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({ ...prev, favorite: !isFav }));
      }
    } catch (error) {
      onShowToast?.('Failed to update bookmark', 'error');
    }
  };

  const handleAddComment = async (contentUuid, commentText) => {
    try {
      const result = await ApiService.addComment(contentUuid, commentText);
      const update = (c) => c.uuid === contentUuid
        ? { ...c, commentCount: result.commentCount, comments: [...(c.comments || []), result.comment] }
        : c;
      setResolvedContent(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: [...(prev.comments || []), result.comment]
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to add comment', 'error');
    }
  };

  const handleDeleteComment = async (contentUuid, commentUuid) => {
    try {
      const result = await ApiService.deleteComment(contentUuid, commentUuid);
      const update = (c) => c.uuid === contentUuid
        ? { ...c, commentCount: result.commentCount, comments: (c.comments || []).filter(cm => cm.uuid !== commentUuid) }
        : c;
      setResolvedContent(prev => prev.map(update));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: (prev.comments || []).filter(cm => cm.uuid !== commentUuid)
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to delete comment', 'error');
    }
  };

  const handleDeleteContent = async (contentUuid) => {
    try {
      await ApiService.deleteContent(contentUuid);
      setResolvedContent(prev => prev.filter(c => c.uuid !== contentUuid));
      setManifest(prev => prev ? prev.filter(e => e.uuid !== contentUuid) : prev);
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(null);
      }
      onShowToast?.('Content deleted', 'success');
    } catch (error) {
      onShowToast?.('Failed to delete content', 'error');
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Get weekly callouts from families with member info for progress display
  const weeklyCallouts = (families || [])
    .filter(f => f.weeklyCallout && f.weeklyCallout.question)
    .filter(f => !selectedFamily || f.uuid === selectedFamily.uuid)
    .map(f => {
      const callout = f.weeklyCallout;
      const answered = callout.answeredUserUuids || [];
      const waiting = callout.waitingOnUserUuids || [];
      const total = answered.length + waiting.length;
      const getNameForUuid = (uuid) => {
        const rel = relatives.find(r => r.uuid === uuid);
        return rel?.name || null;
      };
      return {
        ...callout,
        familyName: f.name,
        familyUuid: f.uuid,
        answeredNames: answered.map(getNameForUuid).filter(Boolean),
        waitingNames: waiting.map(getNameForUuid).filter(Boolean),
        answeredCount: answered.length,
        totalCount: total,
      };
    });

  // Upcoming birthdays (within 7 days)
  const upcomingBirthdays = (relatives || [])
    .map(r => {
      const bd = r.birthDate;
      if (!bd?.birthMonth || !bd?.birthDay) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let next = new Date(today.getFullYear(), bd.birthMonth - 1, bd.birthDay);
      if (next < today) next = new Date(today.getFullYear() + 1, bd.birthMonth - 1, bd.birthDay);
      const daysUntil = Math.round((next - today) / (1000 * 60 * 60 * 24));
      if (daysUntil > 7) return null;
      return { relative: r, daysUntil };
    })
    .filter(Boolean)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  // Client-side filter by selected family's targetFamilies
  const displayContent = selectedFamily
    ? resolvedContent.filter(c => !c.targetFamilies || c.targetFamilies.includes(selectedFamily.uuid))
    : resolvedContent;

  const hasMore = manifest && resolvedCountRef.current < manifest.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* Weekly Callouts Link */}
      {weeklyCallouts.length > 0 && (
        <div className="px-4 pt-4 pb-2">
          <div
            onClick={() => onOpenCalloutsHistory?.()}
            className="bg-purple-600/15 border border-purple-500/30 rounded-2xl p-4 cursor-pointer hover:bg-purple-600/25 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-purple-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">Weekly Callouts</p>
                <p className="text-purple-300 text-xs mt-0.5">
                  {weeklyCallouts.length} active {weeklyCallouts.length === 1 ? 'callout' : 'callouts'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Birthdays */}
      {upcomingBirthdays.length > 0 && (
        <div className="px-4 pt-2 pb-2">
          <h2 className="text-lg font-bold text-white mb-3">Upcoming Birthdays</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {upcomingBirthdays.map(({ relative, daysUntil }) => {
              const imgUrl = relative.profile?.profilePictureUrl || relative.profilePictureUrl || null;
              const label = daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`;
              return (
                <div
                  key={`bday-${relative.uuid}`}
                  className="flex-shrink-0 w-36 bg-gradient-to-b from-purple-900/50 to-gray-800 rounded-2xl p-3 text-center"
                >
                  <div className="relative mx-auto w-16 h-16 mb-2">
                    {imgUrl ? (
                      <img src={imgUrl} alt={relative.name} className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center">
                        <span className="text-xl font-semibold text-gray-400">{relative.name?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                      <Cake size={12} className="text-white" />
                    </div>
                  </div>
                  <p className="text-white text-sm font-semibold truncate">{relative.name}</p>
                  <p className="text-purple-300 text-xs mt-0.5">{label}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Memories */}
      {displayContent.length === 0 && !hasMore ? (
        <div className="text-center py-16 px-4">
          <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No memories yet</p>
          <p className="text-sm text-gray-500 mt-2">Content from your family will appear here</p>
        </div>
      ) : (
        <div className="px-4 pt-2">
          <h2 className="text-lg font-bold text-white mb-3">
            Recent Memories
            {manifest && <span className="text-sm font-normal text-gray-500 ml-2">({manifest.length})</span>}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {displayContent.map((content) => {
              const relName = getRelativeName(content);
              const relImage = getRelativeImage(content);
              const isImage = content.type === 'image';
              const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);

              return (
                <div
                  key={content.uuid}
                  onClick={() => openContent(content)}
                  className="relative bg-gray-800 rounded-xl overflow-hidden cursor-pointer aspect-[3/4]"
                >
                  {/* Thumbnail */}
                  {content.previewUrl ? (
                    <img src={content.previewUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      {isImage ? (
                        <Image className="w-10 h-10 text-gray-600" />
                      ) : (
                        <Video className="w-10 h-10 text-gray-600" />
                      )}
                    </div>
                  )}

                  {/* Bottom gradient overlay */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2.5 pt-8">
                    {/* Author */}
                    <div className="flex items-center gap-1.5 mb-1">
                      {relImage ? (
                        <img src={relImage} alt="" className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center">
                          <span className="text-[10px] font-semibold text-gray-300">
                            {relName[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <span className="text-white text-xs font-medium truncate">{relName}</span>
                      <span className="text-gray-400 text-[10px] ml-auto flex-shrink-0">{formatTimeAgo(content.creation)}</span>
                    </div>

                    {/* Question text */}
                    {content.question && (
                      <p className="text-white text-[11px] leading-tight line-clamp-2 mb-1">{content.question.question}</p>
                    )}

                    {/* Engagement icons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleLike(content.uuid); }}
                        className="flex items-center gap-1 transition-colors"
                      >
                        <Heart className={`w-3.5 h-3.5 ${isLiked ? 'text-red-500 fill-red-500' : 'text-gray-300'}`} />
                        {(content.likeCount || 0) > 0 && (
                          <span className="text-gray-300 text-[10px]">{content.likeCount}</span>
                        )}
                      </button>
                      <div className="flex items-center gap-1 text-gray-300">
                        <MessageCircle className="w-3.5 h-3.5" />
                        {(content.commentCount || 0) > 0 && (
                          <span className="text-[10px]">{content.commentCount}</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(content.uuid); }}
                        className="flex items-center gap-1 transition-colors"
                      >
                        <Bookmark className={`w-3.5 h-3.5 ${content.favorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          {hasMore && (
            <div ref={handleSentinel} className="flex justify-center py-6">
              {loadingMore && (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Video/Image Player Overlay */}
      {currentVideo && (
        <VideoPlayer
          content={currentVideo}
          currentUserUuid={currentUserUuid}
          onNext={playNext}
          onPrevious={playPrevious}
          onClose={() => setCurrentVideo(null)}
          onToggleFavorite={() => handleToggleFavorite(currentVideo.uuid)}
          onToggleLike={() => handleToggleLike(currentVideo.uuid)}
          onAddComment={(text) => handleAddComment(currentVideo.uuid, text)}
          onDeleteComment={(commentUuid) => handleDeleteComment(currentVideo.uuid, commentUuid)}
          onDeleteContent={() => handleDeleteContent(currentVideo.uuid)}
          isFavorite={!!currentVideo.favorite}
          isLiked={(currentVideo.likes || []).some(l => l.userUuid === currentUserUuid)}
          onShowToast={onShowToast}
        />
      )}
    </div>
  );
}

export default HomeFeedView;
